import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  TextField,
  Autocomplete,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { CloseOutlined, InfoOutlined } from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { getAllAdminRoles } from "../UserManagementActions";
import { RootState } from "../../../store/store";

const InviteUserStyles = {
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  avatarWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 2,
  },
  formFieldsWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    width: "100%",
  },
  formButtonsWrapper: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: 2,
  },
};

const InviteUser: React.FC<{
  onInvite: (emails: string[], roles: string[]) => void;
  open: boolean;
  onClose: () => void;
}> = ({ onInvite, open, onClose }) => {
  const isMobile = useMediaQuery("(max-width:767px)");
  const dispatch = useAppDispatch();
  const [emails, setEmails] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [rolesError, setRolesError] = useState<string>("");
  const { adminRoles } = useAppSelector(
    (state: RootState) => state.userManagement
  );
  const handleEmailChange = (event: React.ChangeEvent<{}>, value: string[]) => {
    setEmails(value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && inputValue.trim()) {
      event.preventDefault();
      const email = inputValue.trim();
      if (!emails.includes(email)) {
        setEmails((prev) => [...prev, email]);
        setInputValue("");
        setError("");
      } else {
        setError("Email already added");
      }
    }
  };

  const closeModal = () => {
    setEmails([]);
    setError("");
    onClose();
  };

  const handleDelete = (emailToDelete: string) => {
    setEmails((prev) => prev.filter((email) => email !== emailToDelete));
  };

  const handleSubmit = () => {
    let hasError = false;
    if (emails.length === 0) {
      setError("At least one email is required");
      hasError = true;
    } else {
      setError("");
    }
    if (selectedRoles.length === 0) {
      setRolesError("At least one role is required");
      hasError = true;
    } else {
      setRolesError("");
    }
    if (hasError) return;

    onInvite(emails, selectedRoles);
    onClose();
    setEmails([]);
    setSelectedRoles([]);
  };

  useEffect(() => {
    dispatch(getAllAdminRoles());
  }, [dispatch]);

  const isInviteDisabled =
    emails.length === 0 ||
    selectedRoles.length === 0 ||
    adminRoles.length === 0;

  const filteredAdminRoles = adminRoles.filter(
    (role) => role !== "Super Admin"
  );

  return (
    <Dialog
      open={open}
      onClose={closeModal}
      maxWidth="md"
      PaperProps={{ style: { width: "400px" } }}
      sx={{ backdropFilter: "blur(5px)" }}
      id="invite_user_dialog"
      data-test-id="invite_user_dialog"
      aria-label="Invite User Dialog"
    >
      <DialogTitle sx={InviteUserStyles?.dialogTitle}>
        <Typography
          variant="h6"
          id="invite_user_title"
          data-test-id="invite_user_title"
          aria-label="Invite User"
        >
          Invite User
        </Typography>
        <IconButton
          onClick={closeModal}
          id="invite_user_close"
          data-test-id="invite_user_close"
          aria-label="Close Dialog"
        >
          <CloseOutlined />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box>
          <form
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              paddingTop: "8px",
            }}
            id="invite_user_form"
            data-test-id="invite_user_form"
            aria-label="Invite User Form"
          >
            <Box sx={InviteUserStyles?.avatarWrapper}>
              <Box sx={InviteUserStyles?.formFieldsWrapper}>
                <Autocomplete
                  fullWidth
                  multiple
                  freeSolo
                  options={[]}
                  value={emails}
                  onChange={handleEmailChange}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        {...getTagProps({ index })}
                        onDelete={() => handleDelete(option)}
                        sx={{
                          margin: "2px",
                        }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Enter email"
                      onKeyDown={handleKeyDown}
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      error={!!error}
                      helperText={error}
                      sx={{ marginBottom: 2 }}
                      id="invite_user_email"
                      data-test-id="invite_user_email"
                      aria-label="User Email"
                    />
                  )}
                  sx={{
                    "& .MuiAutocomplete-tag": {
                      display: "inline-flex",
                      whiteSpace: "normal",
                    },
                    "& .MuiAutocomplete-inputRoot": {
                      flexWrap: "wrap",
                    },
                  }}
                />
                <Autocomplete
                  multiple
                  options={filteredAdminRoles}
                  value={selectedRoles}
                  onChange={(_, value) => {
                    setSelectedRoles(value);
                    if (value.length > 0) {
                      setRolesError("");
                    }
                  }}
                  openOnFocus
                  disabled={filteredAdminRoles.length === 0}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        {...getTagProps({ index })}
                        sx={{ margin: "2px" }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select roles"
                      placeholder="Choose roles"
                      sx={{ marginBottom: 2 }}
                      error={false}
                      helperText={
                        filteredAdminRoles.length === 0 ? (
                          <Typography color="error">
                            Create a role to enable user invitations
                          </Typography>
                        ) : (
                          rolesError
                        )
                      }
                      id="invite_user_role"
                      data-test-id="test-invite_user_role"
                      aria-label="User Role"
                    />
                  )}
                  sx={{
                    "& .MuiAutocomplete-tag": {
                      display: "inline-flex",
                      whiteSpace: "normal",
                    },
                    "& .MuiAutocomplete-inputRoot": {
                      flexWrap: "wrap",
                    },
                  }}
                />
              </Box>
            </Box>
          </form>

          <Box sx={InviteUserStyles?.formButtonsWrapper}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isInviteDisabled}
              sx={{ width: "max-content", borderRadius: "4px" }}
              id="invite_user_submit"
              data-test-id="invite_user_submit"
              aria-label="Send Invite"
            >
              Invite
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default InviteUser;
